<template>
    <div class="app-container">

        <!-- 页面内容滚动区 -->
        <main class="page-content">
            <!-- 页面标题 -->
            <div class="page-header">
                <h2 class="page-title">单商品计划定时任务</h2>
                <p class="page-description">系统将自动查询可投商品，按规则创建投流计划</p>
            </div>

            <!-- 主要内容：两栏布局 -->
            <div class="content-grid">
                <!-- 左侧：任务创建表单 -->
                <div class="form-column">
                    <!-- 任务创建卡片 -->
                    <div class="card">
                        <h3 class="card-title">
                            <div>
                                <span class="card-icon">➕</span>
                                {{ isEdit ? '编辑' : '创建' }} 定时任务
                            </div>
                            <div class="c_primary font-normal" v-if="isEdit" @click="backToCreate">
                                返回定时创建任务
                            </div>
                        </h3>

                        <form id="taskForm" class="task-form">

                            <!-- 用户代理 -->
                            <div class="form-group">
                                <label class="form-label">代理 <span class="required">*</span></label>
                                <el-select size="medium" v-model="taskForm.agentId" placeholder="请选择代理" clearable
                                    filterable>
                                    <el-option v-for="(option, index) in agentList" :key="index"
                                        :label="option.username" :value="option.id" />
                                </el-select>
                            </div>

                            <!-- 规则名称 -->
                            <div class="form-group">
                                <label class="form-label">规则名称 <span class="required">*</span></label>
                                <input type="text" v-model="taskForm.ruleName" class="form-control"
                                    placeholder="请输入该定时任务的规则名称">
                            </div>

                            <!-- 计划名称规则 -->
                            <div class="form-group">
                                <label class="form-label">计划名称规则</label>
                                <div class="input-with-info">
                                    <input type="text" v-model="taskForm.planNameRule" class="form-control "
                                        placeholder="请输入定时计划-商品名称-商品id-日期-roi">
                                    <div class="info-icon" title="可用变量">
                                        ?
                                        <div class="info-tooltip">
                                            <p>请输入自定义的计划名称</p>
                                            <p>请输入对应的商品名称、商品id、日期和roi</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- 日预算 -->
                            <div class="form-group">
                                <label class="form-label">日预算 (元)</label>
                                <div class="input-with-prefix">
                                    <span class="prefix">¥</span>
                                    <input type="number" placeholder="请输入日预算" v-model="taskForm.dailyBudget" min="0"
                                        step="100" class="form-control">
                                </div>
                            </div>

                            <!-- ROI设置 -->
                            <div class="form-group">
                                <label class="form-label">ROI设置</label>
                                <div class="radio-group">
                                    <label class="radio-option">
                                        <input type="radio" name="roiType" value="1" v-model="taskForm.roiType">
                                        <div class="radio-content">
                                            <div class="radio-text">固定ROI</div>
                                            <div class="roi-input">
                                                <input type="number" placeholder="请输入固定ROI" v-model="taskForm.fixedRoi"
                                                    min="0" step="0.1" class="form-control small-input input400">
                                            </div>
                                        </div>
                                    </label>

                                    <label class="radio-option">
                                        <input type="radio" name="roiType" value="2" v-model="taskForm.roiType">
                                        <div class="radio-content">
                                            <div class="radio-text">根据商品佣金率设置</div>
                                            <button type="button" @click="showCommissionModal = true;"
                                                class="config-btn">
                                                ⚙️ 佣金率-ROI对应配置
                                            </button>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            <!-- 定时设置 -->
                            <div class="form-group">
                                <label class="form-label">定时设置</label>
                                <div :class="{ 'date-time-grid': !isPageSmall }">
                                    <div class="date-time-item">
                                        <label class="small-label">开始日期</label>
                                        <el-date-picker class=" datePicker3" popper-class="datePicker2"
                                            v-model="taskForm.startDate" type="date" placeholder="选择开始日期">
                                        </el-date-picker>
                                    </div>
                                    <div class="date-time-item">
                                        <label class="small-label">开始时间</label>
                                        <el-time-picker class=" datePicker3" v-model="taskForm.startTime"
                                            :picker-options="{
                                                selectableRange: '00:00:00 - 23:59:59'
                                            }" placeholder="选择开始时间">
                                        </el-time-picker>
                                    </div>
                                </div>
                                <div class="date-time-grid">
                                    <div class="date-time-item">
                                        <label class="small-label">定时器结束时间</label>
                                        <el-date-picker v-model="taskForm.endTime" type="datetime" placeholder="选择结束时间">
                                        </el-date-picker>
                                    </div>
                                </div>
                                <div class="form-group">
                                    <label class="small-label">重复方式</label>
                                    <select v-model="taskForm.repeatType" class="form-control">
                                        <option value="1">每天一次</option>
                                        <option value="2">工作日每天一次</option>
                                        <option value="3">每周一次</option>
                                        <option value="4">仅执行一次</option>
                                    </select>
                                </div>
                            </div>

                            <!-- 可投商品说明 -->
                            <div class="info-box">
                                <span class="info-box-icon">ℹ️</span>
                                <div class="info-box-text">
                                    系统将自动查询并筛选符合条件的可投商品，无需手动选择
                                </div>
                            </div>

                            <!-- 千川账户选择 -->
                            <div class="form-group">
                                <label class="form-label">千川账户 (可多选) <span class="required">*</span></label>

                                <!-- 账户选择工具栏 -->
                                <div class="account-tools">
                                    <div class="search-with-icon">
                                        <input type="text" placeholder="搜索账户..." v-model="accountSearchQuery"
                                            class="form-control">
                                        <span class="search-icon">🔍</span>
                                    </div>

                                </div>
                                <div class="pb20">
                                    <button type="button" @click="selectAllAccounts" class="tool-btn">
                                        全选
                                    </button>
                                    <button type="button" @click="deselectAllAccounts" class="tool-btn">
                                        取消全选
                                    </button>
                                </div>
                                <!-- 账户列表 -->
                                <div class="account-list-container">
                                    <div class="account-list">
                                        <div class="account-item" v-for="account in filteredAccounts"
                                            :key="account.advertiserId"
                                            :class="{ 'account-item-main': account.id === 'main' }">
                                            <label class="account-label">
                                                <input :disabled="!account.existsMatch" type="checkbox"
                                                    :value="account.advertiserId" v-model="taskForm.selectedAccounts"
                                                    class="account-checkbox">
                                                <span class="account-name">{{ account.name }} (ID: {{
                                                    account.advertiserId
                                                    }}) </span> <span class="c_ff3939" v-if="!account.existsMatch">
                                                    (已投放,任务名为{{
                                                        account.taskName }})</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <!-- 已选账户数量提示 -->
                                <div class="selected-count">
                                    ✅ 已选择 <span>{{ taskForm.selectedAccounts.length }}</span> 个账户
                                </div>
                            </div>

                            <!-- 提交按钮 -->
                            <button type="button" @click="createTask" class="submit-btn">
                                📅 {{ isEdit ? '编辑' : '创建' }}定时任务
                            </button>
                        </form>
                    </div>
                </div>

                <!-- 右侧：任务列表 -->
                <div class="list-column">
                    <!-- 任务列表标题 -->
                    <div class="list-header">
                        <h3 class="list-title">
                            <span class="list-icon">📋</span>
                            定时任务列表
                        </h3>
                        <div class="list-controls">
                            <el-input type="text" placeholder="搜索任务名称..." clearable v-model="taskListSearch"
                                class=" form-control_100"></el-input>
                            <button @click="refreshTasks" class="refresh-btn">
                                🔍
                            </button>
                        </div>
                    </div>

                    <!-- 任务列表 -->
                    <div class="task-list">
                        <div class="task-card" :class="{ 'isSelected': task.id == isEditId }" v-for="task in tasks"
                            :key="task.id">
                            <div class="task-header">
                                <div class="task-title-section">
                                    <h4 class="task-name">{{ task.ruleName }}</h4>
                                    <p class="task-schedule">
                                        ⏱️ {{ task.action }} | 选中账户: {{ task.accountIds && task.accountIds.length }}
                                        个
                                    </p>
                                </div>
                                <div class="task-actions">
                                    <span class="task-status" :class="task.status ? 'status-running' : 'status-paused'">
                                        {{ task.status ? '运行中' : '已暂停' }}
                                    </span>
                                    <div class="task-buttons">
                                        <button class="task-btn edit-btn" title="编辑" @click="editTask(task)">
                                            ✏️
                                        </button>
                                        <button class="task-btn delete-btn" title="删除" @click="deleteTask(task.id)">
                                            🗑️
                                        </button>
                                        <button class="task-btn" :title="task.status ? '暂停' : '启用'"
                                            @click="toggleTaskStatus(task)"
                                            :class="task.status ? 'pause-btn' : 'start-btn'">
                                            {{ task.status ? '⏸️' : '▶️' }}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div class="task-details">
                                <div class="detail-item">
                                    <p class="detail-label">计划名称规则</p>
                                    <p class="detail-value">{{ task.planNameRule }}</p>
                                </div>
                                <div class="detail-item">
                                    <div class="detail-label">日预算 / ROI</div>
                                    <div class="detail-value flex alcenter">¥{{ task.dailyBudget }} /
                                        <div v-if="task.roiType == 1">{{ task.fixedRoi }}</div>

                                        <div v-else>
                                            <el-popover placement="top" width="150" trigger="hover">
                                                <div class="">
                                                    <div class=" mb10 flex alcenter"
                                                        v-for="(item, index) in task.commissionRoiList" :key="index">
                                                        <div class="pr20 fs16">
                                                            佣金率范围
                                                            <div class="fs14">
                                                                {{ item.minCommission }} ~ {{ item.maxCommission }}
                                                            </div>
                                                        </div>
                                                        <div class="fs16">
                                                            目标ROI
                                                            <div class="fs14">
                                                                {{ item.targetRoi }}
                                                            </div>
                                                        </div>

                                                    </div>
                                                </div>
                                                <div slot="reference" class="c_primary"> 按佣金率计算
                                                </div>

                                            </el-popover>
                                        </div>
                                    </div>

                                </div>
                                <div class="detail-item">
                                    <p class="detail-label">选中账户</p>
                                    <div class="detail-value detail-value2">
                                        <div class="more-accounts" v-for="(item, ind) in task.accountIds" :key="ind"> {{
                                            item.name }}({{
                                                item.advertiserId }})</div>
                                    </div>
                                </div>
                            </div>

                            <div class="task-footer">
                                <div class="task-meta">
                                    <span>开始日期: {{ task.startDate + ' ' + task.startTime }}</span>
                                    <span class="separator">•</span>
                                    <span>结束日期: {{ task.endTime }}</span>
                                </div>
                                <button class="view-logs-btn" @click="showTaskLogs(task)">
                                    🕒 查看日志
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- 分页 -->
                    <el-pagination class="flex_wrap flex mt20" :small="isPageSmall" background
                        @size-change="sizeChange($event, 0)" @current-change="currentChange($event, 0)"
                        :current-page="page.current" :page-sizes="page.sizes" :page-size="page.size"
                        :total="page.totalSize" layout="total, sizes, prev, pager, next, jumper">
                    </el-pagination>
                </div>
            </div>
        </main>

        <!-- 日志弹窗 -->
        <div class="modal-backdrop" :class="{ 'modal-visible': showLogModal }">
            <div class="modal">
                <div class="modal-header">
                    <h3 class="modal-title">
                        <span class="modal-icon">🕒</span>
                        任务执行日志
                    </h3>
                    <button @click="showLogModal = false; page2.current = 1; page2.size = 10" class="close-btn">
                        ×
                    </button>
                </div>

                <div class="modal-body">
                    <div class="log-task-info">
                        <h4 class="log-task-name">{{ currentTaskLogName }}</h4>
                        <p class="log-task-desc">该任务创建的所有计划记录</p>
                    </div>
                    <div class="modal-controls">
                        <div class="search-with-icon modal-search">
                            <el-select :class="{ 'input150': !isPageSmall }" @change="changeADAccount"
                                class="mb20 responsiveInput2" v-model="selectedADAccount" placeholder="请选择广告主">
                                <el-option v-for="item in currentTaskAccountIds" :key="item.advertiserId"
                                    :label="item.name" :value="item.advertiserId">
                                </el-option>
                            </el-select>
                            <!-- <input type="text" placeholder="搜索日志..." v-model="logSearchQuery"
                                class="form-control form-control_100">
                            <span class="search-icon">🔍</span> -->
                        </div>
                    </div>
                    <!-- 日志列表 -->
                    <div class="log-table-container">
                        <table class="log-table">
                            <thead class="log-table-header">
                                <tr>
                                    <th>千川账户名称</th>
                                    <th>计划名称</th>
                                    <!-- <th>ROI</th> -->
                                    <th>日预算</th>
                                    <th>执行时间</th>
                                    <th>状态</th>
                                    <th>失败原因</th>
                                </tr>
                            </thead>
                            <tbody class="log-table-body">
                                <tr class="log-table-row" v-for="log in filteredLogs" :key="log.id">
                                    <td>{{ log.accountName }}</td>
                                    <td style="width:300px;">{{ log.planName }}</td>
                                    <!-- <td>{{ log.roi }}</td> -->
                                    <td>¥{{ log.dailyBudget }}</td>
                                    <td>{{ log.executeTime }}</td>
                                    <td style="width:50px;">
                                        <span class="log-status"
                                            :class="log.status ? 'status-failed' : 'status-success'">
                                            {{ log.status ? '失败' : '成功' }}
                                        </span>
                                    </td>
                                    <td style="width:300px;">{{ log.status ? log.remark : '' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- 日志分页 -->

                    <!-- 分页 -->
                    <el-pagination class="flex_wrap flex mt20" :small="isPageSmall" background
                        @size-change="sizeChange($event, 1)" @current-change="currentChange($event, 1)"
                        :current-page="page2.current" :page-sizes="page2.sizes" :page-size="page2.size"
                        :total="page2.totalSize" layout="total, sizes, prev, pager, next, jumper">
                    </el-pagination>
                </div>

                <div class="modal-footer">
                    <button @click="showLogModal = false; page2.current = 1; page2.size = 10" class="modal-btn">
                        关闭
                    </button>
                </div>
            </div>
        </div>

        <!-- 佣金率-ROI配置弹窗 -->
        <div class="modal-backdrop" :class="{ 'modal-visible': showCommissionModal }">
            <div class="modal modal-sm">
                <div class="modal-header">
                    <h3 class="modal-title">
                        <span class="modal-icon">🧮</span>
                        佣金率-ROI对应配置
                    </h3>
                    <button @click="showCommissionModal = false" class="close-btn">
                        ×
                    </button>
                </div>

                <div class="modal-body">
                    <p class="modal-description">设置不同佣金率对应的目标ROI值，系统将根据商品实际佣金率自动匹配</p>

                    <div class="commission-configs">
                        <div class="commission-config-row" v-for="(config, index) in commissionConfigs" :key="index">
                            <div class="config-item">
                                <label class="small-label">佣金率范围</label>
                                <div class="range-input">
                                    <input min="0" type="number" @blur="checkCommission" v-model="config.minCommission"
                                        class="form-control small-input small-input-popup" placeholder="最小">
                                    <span class="range-separator">~</span>
                                    <input min="0" type="number" @blur="checkCommission" v-model="config.maxCommission"
                                        class="form-control small-input small-input-popup" placeholder="最大">
                                    <span class="range-suffix">%</span>
                                </div>
                            </div>
                            <div class="config-item">
                                <label class="small-label">目标ROI</label>
                                <div class="roi-input">
                                    <input type="targetRoi" v-model="config.targetRoi" min="0" step="0.1"
                                        class="form-control small-input small-input-popup">
                                </div>
                            </div>
                            <div class="config-actions">
                                <button class="delete-config-btn" @click="removeCommissionConfig(index)">
                                    🗑️
                                </button>
                            </div>
                        </div>
                    </div>

                    <button class="add-config-btn" @click="addCommissionConfig">
                        ➕ 添加佣金率范围
                    </button>
                </div>

                <div class="modal-footer">
                    <button @click="showCommissionModal = false" class="modal-btn secondary-btn">
                        取消
                    </button>
                    <button @click="saveCommissionConfigs" class="modal-btn">
                        保存配置
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            isEdit: false,
            isEditId: '',
            isPageSmall: false,
            currentTaskLogId: '',
            currentTaskAccountIds: [],
            selectedADAccount: '',

            // 搜索相关
            searchQuery: '',
            accountSearchQuery: '',
            taskListSearch: '',
            logSearchQuery: '',

            // 任务表单数据
            taskForm: {
                ruleName: '',
                planNameRule: '',
                dailyBudget: '',
                roiType: 1,
                fixedRoi: '',
                startDate: '',
                startTime: '',
                repeatType: 1,
                selectedAccounts: [],
                endTime: ''
            },

            //分页
            page: {
                //当前页
                current: 1,
                //当前每页显示条数
                size: 10,
                //可选数据显示条数
                sizes: [10, 20, 30, 40, 50, 100],
                //总数据条数
                totalSize: 0,
                //总页数
                totalPages: 0,
                //实际显示条数
                realSize: 0,
            },
            //分页2
            page2: {
                //当前页
                current: 1,
                //当前每页显示条数
                size: 10,
                //可选数据显示条数
                sizes: [10, 20, 30, 40, 50, 100],
                //总数据条数
                totalSize: 0,
                //总页数
                totalPages: 0,
                //实际显示条数
                realSize: 0,
            },
            // 账户数据
            accounts: [],

            // 任务列表数据
            tasks: [

            ],

            // 分页相关
            currentPage: 1,
            pageSize: 10,

            // 弹窗状态
            showLogModal: false,
            showCommissionModal: false,

            // 当前日志任务信息
            currentTaskLogName: '',

            // 任务日志数据
            taskLogs: [

            ],
            agentList: [],
            // 佣金率配置
            commissionConfigs: [
                { minCommission: '', maxCommission: '', targetRoi: '' },
            ]
        };
    },
    mounted() {
        this.getADAccountList()
        this.getAgentList()
        this.getTaskList()
        this.resizeHandler()
        this.getNewCommissionRoi()
        this.debouncedResize = this.debounce(this.resizeHandler, 50);
        window.addEventListener('resize', this.debouncedResize);

    },
    watch: {

    },
    computed: {
        // 筛选后的账户列表
        filteredAccounts() {
            if (!this.accountSearchQuery) {
                return this.accounts;
            }

            const query = this.accountSearchQuery.toLowerCase();
            return this.accounts.filter(account =>
                account.name.toLowerCase().includes(query) ||
                account.advertiserId.toString().includes(query)
            );
        },

        // 筛选后的日志列表
        filteredLogs() {
            if (!this.logSearchQuery) {
                return this.taskLogs;
            }

            const query = this.logSearchQuery.toLowerCase();
            return this.taskLogs.filter(log =>
                log.accountName.toLowerCase().includes(query) ||
                log.planName.toLowerCase().includes(query)
            );
        },

    },

    methods: {
        getNewCommissionRoi() {
            this.$axios.get({
                url: '/qianchuanCommissionRoi/getTheLatestConfiguration',
                success: (res) => {
                    console.log('5555555', res)
                    if (res.data && res.data.length) {
                        this.commissionConfigs = res.data
                    }
                    //          
                },
                error: () => {
                    this.$message.error('出错了')
                },
            })
        },
        checkCommission() {
            this.commissionConfigs.forEach((obj, index) => {
                if (this.commissionConfigs[index - 1] && this.commissionConfigs[index - 1].maxCommission && obj.minCommission) {
                    if (JSON.parse(obj.minCommission) <= JSON.parse(this.commissionConfigs[index - 1].maxCommission)) {
                        this.$set(obj, 'minCommission', (JSON.parse(this.commissionConfigs[index - 1].maxCommission) + 0.01).toFixed(2))
                    }
                }
                console.log(obj.maxCommission <= obj.minCommission, obj.maxCommission, obj.minCommission)
                if (!obj.maxCommission || !obj.minCommission) return
                if (JSON.parse(obj.maxCommission) <= JSON.parse(obj.minCommission)) {
                    this.$set(obj, 'maxCommission', (JSON.parse(obj.minCommission) + 0.01).toFixed(2))
                }

            })
        },
        changeADAccount(e) {
            console.log(e)
            this.getTaskLogList()
        },
        backToCreate() {
            this.isEdit = false
            this.isEditId = null
            this.taskForm = {
                ruleName: '',
                planNameRule: '',
                dailyBudget: '',
                roiType: 1,
                fixedRoi: '',
                startDate: '',
                startTime: '',
                repeatType: 1,
                selectedAccounts: [],
                endTime: ''
            }
            this.getNewCommissionRoi()
            this.commissionConfigs = [
                { minCommission: '', maxCommission: '', targetRoi: '' },
            ]
        },

        //改变当前显示条数
        sizeChange(val, type) {
            if (type) {
                this.page2.size = val
                this.getTaskLogList()

            } else {
                this.page.size = val
                this.getTaskList()
            }
        },
        //改变当前页
        currentChange(val, type) {

            if (type) {
                this.page2.current = val
                this.getTaskLogList()

            } else {
                this.page.current = val
                this.getTaskList()
            }
        },
        debounce(fn, delay) {
            let timer = null;
            return function (...args) {
                clearTimeout(timer);
                timer = setTimeout(() => {
                    fn.apply(this, args);
                }, delay);
            };
        },
        resizeHandler() {
            const viewportWidth = window.innerWidth;
            if (viewportWidth < 500) {
                this.isPageSmall = true
            } else {
                this.isPageSmall = false
            }
        },
        pickDate(e, type) {
            console.log(e, type)
            // 检查 e 是否是有效的 Date 对象
            if (!e || !(e instanceof Date) || isNaN(e.getTime())) {
                return e; // 返回空字符串或其他默认值
            }

            // 处理有效日期对象
            if (!type) {
                const year_start = e.getFullYear()
                const month_start = String(e.getMonth() + 1).padStart(2, '0')
                const day_start = String(e.getDate()).padStart(2, '0')
                return `${year_start}-${month_start}-${day_start}`
            } else if (type == 1) {
                const hours = String(e.getHours()).padStart(2, '0')
                const minutes = String(e.getMinutes()).padStart(2, '0')
                const seconds = String(e.getSeconds()).padStart(2, '0')
                return `${hours}:${minutes}:${seconds}`
            } else if (type == 2) {
                const year_start = e.getFullYear()
                const month_start = String(e.getMonth() + 1).padStart(2, '0')
                const day_start = String(e.getDate()).padStart(2, '0')
                const hours = String(e.getHours()).padStart(2, '0')
                const minutes = String(e.getMinutes()).padStart(2, '0')
                const seconds = String(e.getSeconds()).padStart(2, '0')
                return `${year_start}-${month_start}-${day_start} ${hours}:${minutes}:${seconds}`
            }
        },
        getADAccountList() {
            this.$axios.get({
                url: '/qianchuanScheduleTask/getAbleInvestAccount',
                success: (res) => {
                    console.log('333', res)
                    this.accounts = res.data.data

                    //          
                },
                error: () => {
                    this.$message.error('出错了')
                },
            })
        },

        // 全选账户
        selectAllAccounts() {
            this.taskForm.selectedAccounts = this.accounts.map((account) => {
                if (!account.existsMatch) return
                return account.advertiserId
            });
        },

        // 取消全选账户
        deselectAllAccounts() {
            this.taskForm.selectedAccounts = [];
        },
        getTaskList() {
            this.$axios.post({
                url: '/qianchuanScheduleTask/listByCondition',
                data: {
                    pageIndex: this.page.current,
                    pageSize: this.page.size,
                    ruleName: this.taskListSearch,
                },
                success: (res) => {
                    console.log('tasks', res)
                    if (res.code == 0) {
                        this.tasks = res.data.content
                        this.tasks.forEach((obj) => {
                            switch (obj.repeatType) {
                                case 1:
                                    this.$set(obj, 'action', '每天一次')
                                    break;

                                case 2:
                                    this.$set(obj, 'action', '工作日每天一次')
                                    break;

                                case 3:
                                    this.$set(obj, 'action', '每周一次')
                                    break;

                                case 4:
                                    this.$set(obj, 'action', '仅执行一次')
                                    break;

                            }
                        })
                        this.page.hasMore = res.hasMore
                        this.page.current = res.data.pageable.pageNumber + 1
                        this.page.size = res.data.pageable.pageSize
                        this.page.totalSize = res.data.totalElements
                        this.page.totalPages = res.data.totalPages
                        this.page.realSize = res.data.content.length
                        this.page.sizes = []
                        for (let i = 1; i <= Math.ceil(this.page.totalSize / 10); i++) {
                            this.page.sizes.push(i * 10)
                            if (i === 10) {
                                break
                            }
                        }
                    } else {
                        this.$message.error(res.msg || '出错了')

                    }
                    //          
                },
                error: () => {
                    this.$message.error('出错了')
                },
            })
        },
        // 创建新任务
        createTask() {
            if (!this.taskForm.agentId) {
                this.$message.error('请选择代理')
                return
            }
            if (!this.taskForm.ruleName) {
                this.$message.error('请输入规则名称')
                return
            }
            if (!this.taskForm.planNameRule) {
                this.$message.error('请输入计划名称规则')
                return
            }
            if (!this.taskForm.dailyBudget) {
                this.$message.error('请输入日预算')
                return
            }
            if (this.taskForm.dailyBudget < 300) {
                this.$message.error('日预算不能低于300')
                return
            }
            if (this.taskForm.roiType == 1) {
                if (!this.taskForm.fixedRoi) {
                    this.$message.error('请设置固定ROI')
                    return
                }
            } else {
                let isBack = false
                // let isContain = false
                this.commissionConfigs.forEach((obj) => {
                    if (obj.minCommission === '' || obj.maxCommission === '' || obj.targetRoi === '') {
                        this.$message.error('佣金率不能为空')
                        isBack = true
                        return
                    }

                    // if (obj.minCommission == this.taskForm.dailyBudget || obj.maxCommission == this.taskForm.dailyBudget) {
                    //     isContain = true
                    //     return
                    // }
                })
                if (isBack) return

                // if (!isContain) {
                //     this.$message.error('佣金率范围必须包含日预算，即必须包含 最小 ~ 日预算 这一项')
                //     return
                // }

            }
            if (!this.taskForm.startDate) {
                this.$message.error('请选择开始日期')
                return
            }
            if (!this.taskForm.startTime) {
                this.$message.error('请选择开始时间')
                return
            }
            if (!this.taskForm.selectedAccounts.length) {
                this.$message.error('请选择千川账户')
                return
            }


            let data = {
                agentId: this.taskForm.agentId,
                ruleName: this.taskForm.ruleName,
                planNameRule: this.taskForm.planNameRule,
                dailyBudget: this.taskForm.dailyBudget,
                roiType: this.taskForm.roiType,
                fixedRoi: this.taskForm.roiType == 1 ? this.taskForm.fixedRoi : null,
                startDate: this.pickDate(this.taskForm.startDate, 0),
                startTime: this.pickDate(this.taskForm.startTime, 1),
                repeatType: this.taskForm.repeatType,
                accountIds: this.taskForm.selectedAccounts,
                status: 1,
                commissionRoiList: this.commissionConfigs,
                endTime: this.pickDate(this.taskForm.endTime, 2)
            }
            console.log('data_参数', data)
            let url = '/qianchuanScheduleTask/add'
            if (this.isEdit) {
                data.id = this.isEditId
                url = '/qianchuanScheduleTask/update'
            }

            this.$axios.post({
                url,
                data,
                success: (res) => {
                    // this.loading = false
                    console.log('333', res)
                    if (res.code == 0) {
                        this.$message.success(this.isEdit ? '编辑定时计划成功' : '创建定时计划成功')
                        this.taskForm = {
                            ruleName: '',
                            planNameRule: '',
                            dailyBudget: '',
                            roiType: 1,
                            fixedRoi: '',
                            startDate: '',
                            startTime: '',
                            repeatType: 1,
                            selectedAccounts: [],
                            endTime: ''
                        }
                        this.commissionConfigs = [
                            { minCommission: '', maxCommission: '', targetRoi: '' },
                        ]
                        this.getTaskList()
                        this.getNewCommissionRoi()
                        this.isEdit = false
                        this.isEditId = ''
                    } else {
                        this.$message.error(res.msg || '出错了')

                    }
                },
                error: () => {
                    this.$message.error('出错了')
                },
            })
        },

        getAgentList() {
            this.$axios.post({
                url: '/agent/getAll',
                success: (res) => {
                    this.loading = false
                    console.log('333', res)
                    if (res.code == 0) {
                        this.agentList = res.data
                        console.log('333', this.agentList)

                    } else {
                        this.$message.error(res.msg || '出错了')

                    }
                    //          
                },
                error: () => {
                    this.loading = false
                    this.$message.error('出错了')
                },
            })
        },
        // 获取账户预览文本
        getAccountPreview() {
            if (this.taskForm.selectedAccounts.length === 0) {
                return '未选择账户';
            }

            const selectedAccountNames = this.taskForm.selectedAccounts
                .map(id => {
                    const account = this.accounts.find(acc => acc.id === id);
                    return account ? account.name.split(' - ')[0] : '';
                })
                .filter(name => name);

            return selectedAccountNames.length > 0 ? selectedAccountNames[0] + (selectedAccountNames.length > 1 ? `、${selectedAccountNames[1]}` : '') : '';
        },

        // 刷新任务列表
        refreshTasks() {
            // 模拟刷新
            console.log('刷新任务列表');
            this.getTaskList()
        },

        // 编辑任务
        editTask(task) {
            console.log('编辑任务:', task);
            this.isEdit = true
            this.isEditId = task.id
            const today = new Date()
            const [hours, minutes, seconds] = task.startTime.split(':').map(Number);
            let arr = []
            task.accountIds.forEach((obj) => {
                arr.push(obj.advertiserId)
            })
            this.taskForm = {
                agentId: task.agentId,
                ruleName: task.ruleName,
                planNameRule: task.planNameRule,
                dailyBudget: task.dailyBudget,
                roiType: task.roiType,
                fixedRoi: task.roiType == 1 ? task.fixedRoi : '',
                startDate: new Date(task.startDate),
                startTime: new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes, seconds),
                repeatType: task.repeatType,
                selectedAccounts: arr,
                status: task.status,
                endTime: task.endTime
            }


            this.commissionConfigs = task.commissionRoiList

        },

        // 删除任务
        deleteTask(taskId) {
            this.$axios.post({
                url: 'qianchuanScheduleTask/delete/' + taskId,
                success: (res) => {
                    // this.loading = false
                    console.log('333', res)
                    if (res.code == 0) {
                        this.$message.success('删除成功')
                        this.getTaskList()
                        this.getADAccountList()
                    } else {
                        this.$message.error(res.msg || '出错了')
                    }
                },
                error: () => {
                    this.$message.error('出错了')
                },
            })
        },

        // 切换任务状态（运行/暂停）
        toggleTaskStatus(task) {
            let arr = []
            task.accountIds.forEach((obj) => {
                arr.push(obj.advertiserId)
            })
            task.accountIds = arr
            task.status = task.status ? 0 : 1
            console.log(task)
            this.$axios.post({
                url: '/qianchuanScheduleTask/update',
                data: task,
                success: (res) => {
                    // this.loading = false
                    console.log('333', res)
                    if (res.code == 0) {
                        this.$message.success(task.status ? '启动成功' : '暂停成功')
                        this.getTaskList()
                    } else {
                        this.$message.error(res.msg || '出错了')
                    }
                },
                error: () => {
                    this.$message.error('出错了')
                },
            })
        },

        // 显示任务日志
        showTaskLogs(task) {
            this.currentTaskLogName = task.name;
            this.showLogModal = true;
            this.currentTaskLogId = task.id
            this.currentTaskAccountIds = task.accountIds
            this.selectedADAccount = task.accountIds[0].advertiserId
            this.getTaskLogList()


        },
        getTaskLogList() {
            console.log('qianchuanTaskLog/listByCondition?pageIndex=' + this.page2.current + '&pageSize=' + this.page2.size + '&taskId=' + this.currentTaskLogId + '&accountId=' + this.selectedADAccount, this.currentTaskAccountIds)
            this.$axios.get({
                url: 'qianchuanTaskLog/listByCondition?pageIndex=' + this.page2.current + '&pageSize=' + this.page2.size + '&taskId=' + this.currentTaskLogId + '&accountId=' + this.selectedADAccount,
                success: (res) => {
                    console.log('111', res)
                    if (res.code == 0) {
                        this.taskLogs = res.data.content
                        this.taskLogs.forEach((obj) => {
                            this.$set(obj, 'executeTime', this.formatTimestamp(obj.executeTime))
                        })
                        this.page2.hasMore = res.hasMore
                        this.page2.current = res.data.pageable.pageNumber + 1
                        this.page2.size = res.data.pageable.pageSize
                        this.page2.totalSize = res.data.totalElements
                        this.page2.totalPages = res.data.totalPages
                        this.page2.realSize = res.data.content.length
                        this.page2.sizes = []
                        for (let i = 1; i <= Math.ceil(this.page2.totalSize / 10); i++) {
                            this.page2.sizes.push(i * 10)
                            if (i === 10) {
                                break
                            }
                        }
                    } else {
                        this.$message.error(res.msg || '出错了')

                    }
                    //          
                },
                error: () => {
                    this.$message.error('出错了')
                },
            })
        },
        formatTimestamp(timestamp) {
            const date = new Date(timestamp);

            // 获取日期时间组件
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0'); // 月份从0开始
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');

            // 组合成 YYYY-MM-DD HH:mm:ss 格式
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        },
        // 分页相关
        changePage(page) {
            if (page < 1 || page > this.totalPages) {
                return;
            }
            this.currentPage = page;
        },

        // 佣金率配置相关
        addCommissionConfig() {
            this.commissionConfigs.push({ minCommission: '', maxCommission: '', targetRoi: '' });
        },

        removeCommissionConfig(index) {
            if (this.commissionConfigs.length > 1) {
                this.commissionConfigs.splice(index, 1);
            } else {
                this.$message.error('至少保留一条配置')
            }
        },

        saveCommissionConfigs() {
            // 保存配置逻辑
            console.log('保存佣金率配置:', this.commissionConfigs);
            this.taskForm.roiType = 2;
            let isEmpty = false
            this.commissionConfigs.forEach((obj) => {
                if (obj.minCommission == '' || obj.maxCommission == '' || obj.targetRoi == '') {
                    isEmpty = true
                    return
                }

            })
            if (isEmpty) {
                this.$message.error('配置不能为空')
                return
            }
            this.showCommissionModal = false;
            this.$message.success('佣金率配置保存成功')
        }
    }
};
</script>

<style>
.form-column {
    max-height: 65vh;
    overflow: scroll;
}


/* 全局样式重置与基础设置 */
/* * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
} */

body {
    /* background-color: #f5f7fa; */
    color: #1d2129;
    font-size: 14px;
    line-height: 1.5;
}

/* 布局容器 */
.app-container {
    background-color: #f5f7fa;
}



.sidebar-collapsed {
    margin-left: 0;
}

.menu-toggle {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: #4e5969;
    display: none;
}

.search-container {
    position: relative;
    max-width: 400px;
    width: 100%;
    margin: 0 24px;
}

.search-input {
    width: 100%;
    padding: 8px 12px 8px 36px;
    border: 1px solid #e5e6eb;
    border-radius: 6px;
    font-size: 14px;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.search-input:focus {
    outline: none;
    border-color: #165dff;
    box-shadow: 0 0 0 2px rgba(22, 93, 255, 0.2);
}

.search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #86909c;
}

.user-area {
    display: flex;
    align-items: center;
}

.notification-btn {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: #4e5969;
    position: relative;
    margin-right: 16px;
}

.notification-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    background-color: #ff4d4f;
    color: white;
    font-size: 12px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.user-info {
    display: flex;
    align-items: center;
}

.user-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    margin-right: 8px;
}

.username {
    font-size: 14px;
    color: #1d2129;
    display: none;
}

/* 页面内容 */
.page-content {
    padding: 24px;
    overflow-y: auto;
    /* height: calc(100vh - 60px); */
}

.page-header {
    margin-bottom: 24px;
}

.page-title {
    font-size: 24px;
    font-weight: 600;
    color: #1d2129;
    margin-bottom: 8px;
}

.page-description {
    color: #86909c;
}

/* 网格布局 */
.content-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
}

@media (min-width: 1024px) {
    .content-grid {
        grid-template-columns: 1fr 2fr;
    }
}

/* 卡片样式 */
.card {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    transition: box-shadow 0.3s, transform 0.3s;
}

.card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
}

.card-title {
    font-size: 16px;
    font-weight: 600;
    color: #1d2129;
    padding: 20px 24px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.card-icon {
    margin-right: 8px;
    color: #165dff;
}

/* 表单样式 */
.task-form {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.form-group {
    margin-bottom: 4px;
}

.form-label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #4e5969;
}

.required {
    color: #ff4d4f;
}

.form-control {
    width: 400px;
    box-sizing: border-box;
    padding: 10px 12px;
    border: 1px solid #e5e6eb;
    border-radius: 6px;
    font-size: 14px;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control_100 {
    width: 100%;
}

.form-control:focus {
    outline: none;
    border-color: #165dff;
    box-shadow: 0 0 0 2px rgba(22, 93, 255, 0.2);
}

.small-input {
    /* width: auto; */
    /* flex: 1; */
    width: 150px !important;
}

/* 带图标的输入框 */
.input-with-info {
    position: relative;
}

.info-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    background-color: #f5f5f5;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: help;
    font-size: 12px;
    color: #86909c;
}

.info-tooltip {
    visibility: hidden;
    position: absolute;
    right: 30px;
    top: 0;
    background-color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    padding: 12px;
    width: 200px;
    font-size: 12px;
    color: #4e5969;
    z-index: 10;
}

.info-icon:hover .info-tooltip {
    visibility: visible;
}

.tooltip-title {
    font-weight: 600;
    margin-bottom: 6px;
}

/* 带前缀的输入框 */
.input-with-prefix {
    position: relative;
}

.prefix {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #86909c;
}

.input-with-prefix .form-control {
    padding-left: 32px;
}

/* 单选按钮组 */
.radio-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.radio-option {
    display: flex;
    cursor: pointer;
}

.radio-option input {
    margin-top: 4px;
}

.radio-content {
    flex: 1;
    margin-left: 12px;
    padding: 12px;
    border: 1px solid #e5e6eb;
    border-radius: 6px;
    transition: border-color 0.2s;
}

.radio-option:hover .radio-content {
    border-color: rgba(22, 93, 255, 0.5);
}

.radio-option input:checked+.radio-content {
    border-color: #165dff;
}

.radio-text {
    font-weight: 500;
    margin-bottom: 8px;
}

.roi-input {
    display: flex;
    align-items: center;
    gap: 8px;
}

.roi-suffix {
    color: #86909c;
    white-space: nowrap;
}

/* 日期时间选择 */
.date-time-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 12px;
}

.date-time-item {
    flex: 1;
}

.small-label {
    display: block;
    margin-bottom: 6px;
    font-size: 12px;
    color: #86909c;
}

/* 信息框 */
.info-box {
    background-color: #f5f7fa;
    border: 1px solid #e5e6eb;
    border-radius: 6px;
    padding: 12px;
    display: flex;
    align-items: flex-start;
}

.info-box-icon {
    margin-right: 8px;
    color: #165dff;
    margin-top: 2px;
}

.info-box-text {
    flex: 1;
    font-size: 13px;
    color: #4e5969;
}

/* 账户选择区域 */
.account-tools {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
}

.search-with-icon {
    position: relative;
    flex: 1;
    min-width: 180px;
}

.search-with-icon .form-control {
    padding-left: 36px;
}

.search-with-icon .search-icon {
    left: 12px;
}

.tool-btn {
    padding: 8px 12px;
    border: 1px solid #e5e6eb;
    border-radius: 6px;
    background-color: #fff;
    color: #4e5969;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.2s;
}

.tool-btn:hover {
    background-color: #f5f5f5;
}

.tool-btn:active {
    transform: scale(0.98);
}

.account-list-container {
    border: 1px solid #e5e6eb;
    border-radius: 6px;
    overflow: hidden;
}

.account-list {
    max-height: 250px;
    overflow-y: auto;
}

.account-item {
    padding: 8px;
    border-bottom: 1px solid #f0f0f0;
}

.account-item-main {
    background-color: #f5f7fa;
}

.account-label {
    display: flex;
    align-items: center;
    padding: 6px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;
}

.account-label:hover {
    background-color: #f5f5f5;
}

.account-checkbox {
    margin-right: 10px;
}

.account-name {
    font-size: 13px;
    color: #4e5969;
}

.selected-count {
    margin-top: 8px;
    font-size: 12px;
    color: #86909c;
}

/* 提交按钮 */
.submit-btn {
    width: 100%;
    padding: 12px;
    background-color: #ff7d00;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.submit-btn:hover {
    background-color: #e67000;
}

.submit-btn:active {
    transform: scale(0.98);
}

/* 列表样式 */
.list-header {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 16px;
}

@media (min-width: 640px) {
    .list-header {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
}

.list-title {
    font-size: 16px;
    font-weight: 600;
    color: #1d2129;
    display: flex;
    align-items: center;
}

.list-icon {
    margin-right: 8px;
    color: #165dff;
}

.list-controls {
    display: flex;
    align-items: center;
    gap: 8px;
}

.small-search {
    min-width: auto;
    width: 180px;
}

.refresh-btn {
    width: 36px;
    height: 36px;
    border: 1px solid #e5e6eb;
    border-radius: 6px;
    background-color: #fff;
    color: #4e5969;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.refresh-btn:hover {
    background-color: #f5f5f5;
}

.refresh-btn:active {
    transform: scale(0.95);
}

/* 任务卡片 */
.task-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-height: 55vh;
    overflow: scroll;
}

.task-card {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    transition: box-shadow 0.3s, transform 0.3s;
}

.isSelected {
    border: 1px solid #409eff;
}

.task-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
}

.task-header {
    padding: 20px 24px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

@media (min-width: 640px) {
    .task-header {
        flex-direction: row;
        justify-content: space-between;
    }
}

.task-title-section {
    flex: 1;
}

.task-name {
    font-size: 16px;
    font-weight: 600;
    color: #1d2129;
    margin-bottom: 4px;
}

.task-schedule {
    font-size: 13px;
    color: #86909c;
}

.task-actions {
    display: flex;
    align-items: center;
    gap: 16px;
}

.task-status {
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
}

.status-running {
    background-color: rgba(82, 196, 26, 0.1);
    color: #52c41a;
}

.status-paused {
    background-color: rgba(229, 230, 235, 0.5);
    color: #86909c;
}

.task-buttons {
    display: flex;
    gap: 4px;
}

.task-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.task-btn:hover {
    background-color: #f5f5f5;
}

.task-btn:active {
    transform: scale(0.9);
}

.edit-btn:hover {
    color: #165dff;
}

.delete-btn:hover {
    color: #ff4d4f;
}

.pause-btn:hover {
    color: #faad14;
}

.start-btn:hover {
    color: #52c41a;
}

.task-details {
    padding: 16px 24px;
    border-bottom: 1px solid #f0f0f0;
}

@media (min-width: 768px) {
    .task-details {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 16px;
    }
}

.detail-item {
    margin-bottom: 12px;
}

@media (min-width: 768px) {
    .detail-item {
        margin-bottom: 0;
    }
}

.detail-label {
    font-size: 12px;
    color: #86909c;
    margin-bottom: 4px;
}

.detail-value {
    font-size: 13px;
    color: #4e5969;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}


.detail-value2 {
    overflow: scroll;
    max-height: 100px;
}

.more-accounts {
    color: #c9cdd4;
}

.task-footer {
    padding: 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

@media (min-width: 640px) {
    .task-footer {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    }
}

.task-meta {
    font-size: 12px;
    color: #86909c;
}

.separator {
    margin: 0 8px;
}

.view-logs-btn {
    color: #165dff;
    background: none;
    border: none;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: color 0.2s, transform 0.2s;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 0;
}

.view-logs-btn:hover {
    color: #0e42d2;
}

.view-logs-btn:active {
    transform: scale(0.98);
}

/* 分页样式 */
.pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin-top: 32px;
}

.page-btn {
    width: 32px;
    height: 32px;
    border: 1px solid #e5e6eb;
    border-radius: 4px;
    background-color: #fff;
    color: #4e5969;
    cursor: pointer;
    transition: background-color 0.2s, border-color 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.page-btn:hover:not(:disabled) {
    border-color: #165dff;
    color: #165dff;
}

.page-btn:disabled {
    cursor: not-allowed;
    color: #c9cdd4;
}

.page-number {
    width: 32px;
    height: 32px;
    border: 1px solid #e5e6eb;
    border-radius: 4px;
    background-color: #fff;
    color: #4e5969;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.page-number:hover:not(.current-page) {
    border-color: #165dff;
    color: #165dff;
}

.current-page {
    background-color: #165dff;
    color: #fff;
    border-color: #165dff;
}

/* 弹窗样式 */
.modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s, visibility 0.3s;
}

.modal-visible {
    opacity: 1;
    visibility: visible;
}

.modal {
    background-color: #fff;
    border-radius: 8px;
    width: 90%;
    max-width: 1000px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    transform: scale(0.95);
    transition: transform 0.3s;
}

.modal-visible .modal {
    transform: scale(1);
}

.modal-sm {
    max-width: 600px;
}

.modal-header {
    padding: 16px 24px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.modal-title {
    font-size: 16px;
    font-weight: 600;
    color: #1d2129;
    display: flex;
    align-items: center;
}

.modal-icon {
    margin-right: 8px;
    color: #165dff;
}

.modal-controls {
    display: flex;
    align-items: center;
    gap: 12px;
}

.modal-search {
    width: 200px;
    display: block;
}

.close-btn {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: #86909c;
    transition: color 0.2s;
}

.close-btn:hover {
    color: #1d2129;
}

.modal-body {
    padding: 24px;
    overflow-y: auto;
    flex: 1;
}

.modal-footer {
    padding: 16px 24px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}

.modal-btn {
    padding: 8px 16px;
    background-color: #165dff;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.2s, transform 0.2s;
}

.modal-btn:hover {
    background-color: #0e42d2;
}

.modal-btn:active {
    transform: scale(0.98);
}

.secondary-btn {
    background-color: #fff;
    color: #4e5969;
    border: 1px solid #e5e6eb;
}

.secondary-btn:hover {
    background-color: #f5f5f5;
    color: #1d2129;
}

.modal-description {
    font-size: 13px;
    color: #86909c;
    margin-bottom: 14px;
}

/* 日志表格 */
.log-task-info {
    margin-bottom: 16px;
}

.log-task-name {
    font-size: 16px;
    font-weight: 600;
    color: #1d2129;
    margin-bottom: 4px;
}

.log-task-desc {
    font-size: 13px;
    color: #86909c;
}

.log-table-container {
    overflow-x: auto;
    margin-bottom: 16px;
}

.log-table {
    width: 100%;
    border-collapse: collapse;
}

.log-table-header {
    background-color: #f5f7fa;
}

.log-table th,
.log-table td {
    padding: 12px 16px;
    text-align: left;
    font-size: 13px;
}

.log-table th {
    font-weight: 600;
    color: #86909c;
    border-bottom: 1px solid #e5e6eb;
}

.log-table-row {
    border-bottom: 1px solid #f0f0f0;
}

.log-table-row:hover {
    background-color: #f5f7fa;
}

.log-status {
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
}

.status-success {
    background-color: rgba(82, 196, 26, 0.1);
    color: #52c41a;
}

.status-failed {
    background-color: rgba(255, 77, 79, 0.1);
    color: #ff4d4f;
}

.log-pagination {
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
}

@media (min-width: 640px) {
    .log-pagination {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    }
}

.log-count-info {
    font-size: 13px;
    color: #86909c;
}

.highlight {
    font-weight: 600;
    color: #1d2129;
}

/* 佣金率配置 */
.commission-configs {
    margin-bottom: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.commission-config-row {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex-direction: row;
    align-items: flex-end;
}


.config-item {
    flex: 1;
}

.range-input {
    display: flex;
    align-items: center;
    gap: 8px;
}

.range-separator {
    color: #86909c;
    white-space: nowrap;
}

.range-suffix {
    color: #86909c;
    white-space: nowrap;
    margin-left: 8px;
    display: flex;
    align-items: center;
}

.config-actions {
    display: flex;
    justify-content: flex-end;
}

.delete-config-btn {
    width: 36px;
    height: 36px;
    border: 1px solid #e5e6eb;
    border-radius: 6px;
    background-color: #fff;
    color: #86909c;
    cursor: pointer;
    transition: background-color 0.2s, color 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.delete-config-btn:hover {
    background-color: #fff5f5;
    color: #ff4d4f;
}

.add-config-btn {
    width: 100%;
    padding: 10px;
    border: 1px dashed #c9cdd4;
    border-radius: 6px;
    background-color: #fff;
    color: #86909c;
    font-size: 14px;
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.add-config-btn:hover {
    border-color: #165dff;
    color: #165dff;
}

input:disabled {
    background-color: #f8f9fa;
    color: #6c757d;
    border-color: #dee2e6;
    cursor: not-allowed;
    opacity: 0.7;
}


/* 响应式适配 */
@media (max-width: 1023px) {

    .main-content {
        margin-left: 0;
    }
}

@media (max-width: 767px) {
    .menu-toggle {
        display: block;
    }

    .search-container {
        display: none;
    }

    .username {
        display: none;
    }

    .task-details {
        display: flex;
        flex-direction: column;
    }
}

@media (min-width: 768px) {
    .username {
        display: block;
    }
}

@media (max-width: 768px) {
    .form-control {
        width: 200px;
    }

    .small-input-popup {
        width: 80px !important;
    }


    /deep/ .el-pagination__total {
        padding-bottom: 40px !important;
        display: block !important;
    }


    /deep/ .el-pagination__jump {
        margin-left: 0 !important;
    }

    /deep/ .el-pagination__editor.el-input {
        margin-top: 15px;
        margin-bottom: 15px;
    }

    /deep/ .el-pagination__total {
        font-size: 12px !important;
    }
}
</style>